'use client';

import { DisplayedCarInfoInterface, Showroom } from '@/types';
import styles from './showroomModal.module.scss';
import colors from '@/theme/colors.module.scss';
import ReserveSeeNotify from '@/components/shared/ReserveSeeNotify/ReserveSeeNotify';
import Modal from '@/components/shared/modal.component';
import Carousel from '@/components/shared/carousel/carousel.component';
import Typography from '@/components/shared/typography/typography.component';
import ClientOnly from '@/components/shared/ClientOnly/ClientOnly';
import { useTranslation, useWindowDimensions } from '@/hook';
import { AccessTime, CallMade, Close } from '@mui/icons-material';
import moment from 'moment';
import { ReactNode } from 'react';
import Image from 'next/image';
import Link from '@/components/WrappedLink';
import {
    InfoCircleIcon,
    LocationIcon,
} from '../../../../../../public/Images/Icons';
import { AVAILABLE_LANGUAGES } from '@/constants';

const queryString = 'sylndr';

const modalMobileStyle = {
    margin: '0px',
    width: '100vw',
    maxWidth: 'none',
    alignSelf: 'flex-end',
};

const imageProps = { width: 576, height: 233, quality: 90 };

const modalPadding = 24;

const ShowroomModal = ({
    open,
    lang,
    onClose,
    showroom,
    setNotifyModal,
    seeCarButtonClicked,
    carInfo,
    isDesktop,
}: {
    open: boolean;
    lang: AVAILABLE_LANGUAGES;
    onClose: React.Dispatch<React.SetStateAction<boolean>>;
    carInfo: DisplayedCarInfoInterface;
    showroom: Showroom;
    isDesktop: boolean;
    setNotifyModal: React.Dispatch<React.SetStateAction<boolean>>;
    seeCarButtonClicked: () => void;
}) => {
    const { t } = useTranslation();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();

    const calcImgHeight = () =>
        Math.min(imageProps.height, windowHeight * 0.33);
    const calcImgwidth = () =>
        Math.max(
            460,
            imageProps.height * (imageProps.width / imageProps.height)
        );

    return (
        <ClientOnly>
            <Modal
                open={open}
                setOpen={onClose}
                sx={isDesktop ? {} : modalMobileStyle}
            >
                <div
                    className={
                        isDesktop ? styles.ModalDesktop : styles.ModalMobile
                    }
                >
                    <div
                        className={styles.ModalContent}
                        style={{
                            maxWidth: isDesktop
                                ? `${imageProps.width}px`
                                : 'none',
                        }}
                    >
                        <div className={styles.SectionHeader}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div className={styles.SectionHeaderDetails}>
                                    <Typography
                                        variant={
                                            isDesktop
                                                ? 'subtitle2Medium'
                                                : 'body2Medium'
                                        }
                                        color={colors.gray900}
                                    >
                                        {lang === 'en'
                                            ? showroom.name.en
                                            : showroom.name.ar}
                                    </Typography>
                                    {/* <Typography variant="caption" style={{ color: colors.gray500 }}>
                    {lang === 'en' ? showroom.address.en : showroom.address.ar}
                  </Typography> */}
                                </div>
                                <div
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onClose(false)}
                                >
                                    <Close sx={{ color: colors.gray500 }} />
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                width: isDesktop
                                    ? `${calcImgwidth()}px`
                                    : windowWidth - modalPadding * 2,
                                height: isDesktop
                                    ? `${calcImgHeight()}px`
                                    : (windowWidth - modalPadding * 2) *
                                      (233 / 576),
                                borderRadius: '8px',
                                display: 'flex',
                            }}
                        >
                            <Carousel
                                imgs={showroom.images}
                                containerWidth={''}
                                imageMargin={''}
                                showDots={true}
                                dragable={false}
                                slideShow={false}
                                UseNextImage={true}
                                lang={'en'}
                                showArrows={true}
                                //padding x2 (both sides)
                                width={
                                    isDesktop
                                        ? calcImgwidth()
                                        : windowWidth - modalPadding * 2
                                }
                                height={imageProps.height}
                                quality={imageProps.quality}
                                overflow="hidden"
                                limitPlaceHolderHeight={true}
                                objectFitValue="cover"
                                disablePlaceHolder={true}
                            ></Carousel>
                        </div>
                        <ModalSubSection
                            title={t('Working Hours')}
                            icon={
                                <AccessTime
                                    sx={{ width: '16px', height: '16px' }}
                                    className={styles.IconStyle}
                                />
                            }
                            text={`${moment(showroom.openFrom, 'HH:mm:ss')
                                .locale(lang)
                                .format('hh:mma')} ${t('to')} ${moment(
                                showroom.openTo,
                                'HH:mm:ss'
                            )
                                .locale(lang)
                                .format('hh:mma')}`}
                            isDesktop={isDesktop}
                        />

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                justifyContent: 'space-evenly',
                                gap: '8px',
                            }}
                        >
                            <ModalSubSection
                                title={t('Full Address - modal')}
                                icon={
                                    <Image
                                        src={LocationIcon}
                                        alt={'otherInfoIcon'}
                                        className={styles.IconStyle}
                                        width={16}
                                        height={16}
                                    />
                                }
                                text={
                                    lang === 'en'
                                        ? showroom.address.en
                                        : showroom.address.ar
                                }
                                isDesktop={isDesktop}
                            />
                            <Link
                                className={styles.Directions}
                                href={`https://maps.google.com/?q=${queryString}&ll=${showroom.pinLocation.lat},${showroom.pinLocation.long}$`}
                                target="_blank"
                            >
                                <Typography
                                    variant="body2Medium"
                                    color={colors.gray700}
                                >
                                    {t('directions')}
                                </Typography>
                                <CallMade
                                    sx={{ height: '20px', width: '20px' }}
                                    fill={colors.gray700}
                                />
                            </Link>
                        </div>
                        <ModalSubSection
                            title={t('Instructions to arrive')}
                            icon={
                                <Image
                                    src={InfoCircleIcon}
                                    alt={'otherInfoIcon'}
                                    className={styles.IconStyle}
                                    width={16}
                                    height={16}
                                />
                            }
                            text={
                                lang === 'en'
                                    ? showroom.instructions.en
                                    : showroom.instructions.ar
                            }
                            isDesktop={isDesktop}
                        />

                        <ReserveSeeNotify
                            carInfo={carInfo}
                            lang={lang}
                            setNotifyModal={setNotifyModal}
                            seeCarButtonClicked={seeCarButtonClicked}
                            showTip={false}
                        />
                    </div>
                </div>
            </Modal>
        </ClientOnly>
    );
};

const ModalSubSection = ({
    title,
    icon,
    text,
    isDesktop,
}: {
    title: string;
    icon: ReactNode;
    text: string;
    isDesktop: boolean;
}) => {
    return (
        <div className={styles.ModalSubSection}>
            {title ? (
                <Typography
                    variant={isDesktop ? 'captionSemiBold' : 'captionMedium'}
                    color={colors.gray900}
                >
                    {title}
                </Typography>
            ) : null}
            <div
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
            >
                <div className={styles.IconContainer}>{icon}</div>
                <Typography
                    variant={isDesktop ? 'body2SemiBold' : 'captionSemiBold'}
                    color={colors.gray600}
                >
                    {text}
                </Typography>
            </div>
        </div>
    );
};

export default ShowroomModal;
